import { findService, formatPoints } from "@iec-id/shared";
import { Button, Card, Icon, IconTile, notify } from "@iec-id/ui";
import { useTranslation } from "react-i18next";
import { daysUntil } from "@/lib/format";
import { api } from "@/lib/trpc";
import { useLegalT } from "./i18n";

/**
 * Баннер «аккаунт помечен на удаление» с обратным отсчётом.
 *
 * Состояние читается из `account.status`, а не передаётся пропом из ответа
 * `requestDeletion`. Разница видна ровно после перезагрузки: раньше подробности
 * (дата стирания, сервисы, Баллы) жили в `useState` раздела «Правовой центр», и
 * человек, вернувшийся на витрину назавтра, видел баннер без единой даты — либо
 * не видел вовсе, потому что открыл другой раздел.
 *
 * Отсюда же второе следствие: баннер монтируется в оболочке и виден на всех
 * разделах. Пока идёт отсрочка, решение обратимо, и кнопка отмены обязана быть
 * на виду — это последнее окно, в котором можно вернуть аккаунт вместе с Баллами.
 */
export function DeletionBanner() {
	const t = useLegalT();
	const { i18n } = useTranslation();
	const utils = api.useUtils();

	const status = api.account.status.useQuery(undefined, { retry: false });

	const cancel = api.account.cancelDeletion.useMutation({
		onSuccess: () => {
			void utils.account.status.invalidate();
			void utils.auth.me.invalidate();
			notify.success(t("deletionBanner.cancelled"));
		},
		onError: () => notify.error(t("deletionBanner.cancelFailed")),
	});

	const data = status.data;
	if (!data?.pendingDeletion) return null;

	const services = data.notifiedServices.map((id) => findService(id)?.name ?? id).join(", ");
	// Обратный отсчёт считаем от даты стирания: «осталось 12 дней» человек
	// понимает быстрее, чем календарную дату через месяц.
	const daysLeft = data.scheduledPurgeAt ? daysUntil(data.scheduledPurgeAt) : null;

	return (
		<Card padding="desktop" gap="row" className="p-4 md:p-5">
			<div className="flex items-center gap-2.5">
				<IconTile className="bg-danger-soft text-danger">
					<Icon name="TriangleAlert" />
				</IconTile>
				<span className="t-subsection">{t("deletionBanner.title")}</span>
			</div>

			<p className="t-body text-fg-secondary">
				{data.scheduledPurgeAt
					? t("deletionBanner.withDate", { date: new Date(data.scheduledPurgeAt) })
					: t("deletionBanner.withoutDate", { days: data.graceDays })}
			</p>

			{daysLeft !== null && daysLeft >= 0 ? (
				<p className="t-num-sm tnum text-fg">{t("deletionBanner.daysLeft", { count: daysLeft })}</p>
			) : null}

			{services ? (
				<p className="t-meta text-fg-muted">{t("deletionBanner.services", { list: services })}</p>
			) : null}

			{data.pointsToForfeit > 0 ? (
				<p className="t-meta tnum text-fg-muted">
					{t("deletionBanner.points", {
						points: formatPoints(data.pointsToForfeit, i18n.language),
					})}
				</p>
			) : null}

			{/*
			  `canCancel: false` при `pendingDeletion: true` означает «необратимый
			  прогон уже начался», а не «кнопки нет». Показать кнопку в этот момент
			  значит пообещать отмену, которую сервер отклонит, — поэтому вместо
			  неё идёт прямой текст о том, что происходит.
			*/}
			{data.canCancel ? (
				<Button
					variant="second"
					className="cursor-pointer self-start"
					state={cancel.isPending ? "loading" : "default"}
					onClick={() => cancel.mutate()}
				>
					{t("deletionBanner.cancel")}
				</Button>
			) : (
				<p className="t-meta text-danger">{t("deletionBanner.purging")}</p>
			)}
		</Card>
	);
}
